import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import {
  getAllCategoriesForServiceProvider,
  getAllWorkTypes,
  getStateAndCity,
  checkServiceProviderExists,
  importSP
} from '../../services/api/implementation/impl';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Multiselect from 'multiselect-react-dropdown';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { capitalizeWords } from '../../utils/stringUtils';

// Validation schema including address fields
const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required('Full name is required')
    .max(50, 'Full name should be at most 50 characters'),
  phone_number: yup
    .string()
    .required('Phone number is required')
    .matches(/^[0-9]{10}$/, 'Invalid phone number'),
  email: yup.string().email('Invalid email format').optional(),
  category_id: yup.number().required('User role is required'),
  type_id: yup.string().required('User type is required'),
  zip_code: yup
    .string()
    .required('Zip/Pin code is required')
    .matches(/^[0-9]{6}$/, 'Zip/Pin code should be exactly 6 digits'),
  country: yup.string().required('Country is required').oneOf(['India'], 'Country must be India'),
  state: yup.string().required('State is required'),
  city: yup.string().required('City is required'),
  subCategory: yup.array()
});

export const AddServiceProvider = ({
  onClose,
  onSubmit,
  isSubmitting,
  selectedServiceProvider,
  setSelectedServiceProvider,
  setModalType,
  modalType,
  setOpenAddUser,
  OpenAddUser,
  refetchServiceProviders
}) => {
  const userId = Cookies.get('userId');
  // Initialize react-hook-form with Yup validation
  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: selectedServiceProvider?.user_name || '',
      phone_number: selectedServiceProvider?.user_phone_number || '',
      email: selectedServiceProvider?.user_email || '',
      category_id: selectedServiceProvider?.category_id || '',
      type_id: selectedServiceProvider?.type_id || '',
      zip_code: selectedServiceProvider?.pincode || '',
      country: selectedServiceProvider?.country || 'India',
      state: selectedServiceProvider?.state || '',
      city: selectedServiceProvider?.city || '',
      subCategory: selectedServiceProvider?.subCategory || [],
      is_active: selectedServiceProvider?.is_active || false
    }
  });

  // Local state for address fields
  const [city, setCity] = useState('Select');
  const [state, setState] = useState('-');
  const [cityDropdownOptions, setCityDropdownOptions] = useState([]);

  // Local state for service provider categories
  const [categoriesForDropdown, setCategoriesForDropdown] = useState([]);

  // Marketplace check states
  const [marketplaceSP, setMarketplaceSP] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isCheckingSP, setIsCheckingSP] = useState(false);
  const [spMessage, setSpMessage] = useState('');

  // Fetch all service provider categories
  const { data: allCategories } = useQuery({
    queryKey: ['getAllCategoriesForServiceProvider'],
    queryFn: getAllCategoriesForServiceProvider
  });

  // Mutation for checking service provider existence
  const { mutate: checkSPMutation } = useMutation({
    mutationFn: checkServiceProviderExists,
    onSuccess: (result) => {
      if (result && result?.data?.id) {
        setMarketplaceSP(result);
        // Extract message from the API response
        const message = result.message || '';
        setSpMessage(message);
        setShowImportModal(true);
      }
      setIsCheckingSP(false);
    },
    onError: (err) => {
      setIsCheckingSP(false);
      // Service provider not found - this is expected, no error message needed
    }
  });

  // Mutation for importing service provider
  const { mutate: importSPMutation, isLoading: isImporting } = useMutation({
    mutationFn: importSP,
    onSuccess: (result) => {
      toast.success('Service Provider imported successfully!');
      setShowImportModal(false);
      setMarketplaceSP(null);
      setOpenAddUser(false);
      refetchServiceProviders();
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to import service provider'
      );
    }
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Add Edit Service Provider Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (Array.isArray(allCategories)) {
      setCategoriesForDropdown(allCategories);
    }
  }, [allCategories]);

  const { data: AllWorkTypes, refetch: fetchWorkTypes } = useQuery({
    queryKey: ['AllWorkTypes'],
    queryFn: getAllWorkTypes
  });

  useEffect(() => {
    fetchWorkTypes();
  }, []);

  const CategoryOptions =
    AllWorkTypes?.map((item) => ({
      label: item.name || '',
      id: item.id,
      value: item.id
    })) || [];

  // Handler for pincode changes
  const handlePincodeChange = useCallback(
    async (pincode) => {
      const cleanedPincode = pincode.replace(/[^0-9]/g, '');
      setValue('zip_code', cleanedPincode);

      if (cleanedPincode.length === 6) {
        try {
          const pincodeList = await getStateAndCity(cleanedPincode);
          const postOfficeList = pincodeList[0].PostOffice;

          if (postOfficeList.length > 0) {
            const newState = postOfficeList[0].State;
            // Determine unique circles and tehsils from the post office data
            const uniqueCircles = [
              ...new Set(
                postOfficeList
                  .map((office) => {
                    const circle =
                      office.Name && office.Name !== 'NA' && office.Name.trim() !== ''
                        ? office.Name
                        : office.Block;
                    return circle && circle !== 'NA' && circle.trim() !== '' ? circle : null;
                  })
                  .filter(Boolean)
              )
            ];
            const uniqueTehsils = [...new Set(postOfficeList.map((office) => office.Block))];

            setCityDropdownOptions(
              uniqueTehsils.map((tehsil) => ({ value: tehsil, label: tehsil }))
            );
            const newCity = uniqueCircles[0];

            setValue('state', newState);
            setValue('city', uniqueTehsils[0]);

            setState(newState);
            setCity(newCity);
          } else {
            setValue('state', '-');
            setValue('city', 'Select');
            setCityDropdownOptions([]);
            setState('-');
            setCity('Select');
          }
        } catch (error) {
          toast.error('The entered pincode is invalid. Please enter a valid pincode.');
          setValue('state', '-');
          setValue('city', 'Select');
          setCityDropdownOptions([]);
          setState('-');
          setCity('Select');
        }
      }
    },
    [setValue]
  );

  useEffect(() => {
    if (modalType === 'Edit' && selectedServiceProvider) {
      const {
        user_name,
        user_phone_number,
        user_email,
        category_id,
        type,
        pincode,
        country,
        state,
        city,
        subCategory,
        is_active
      } = selectedServiceProvider;

      setValue('name', user_name || '');
      setValue('phone_number', user_phone_number || '');
      setValue('email', user_email || '');
      setValue('category_id', category_id || '');
      setValue('type_id', type || '');
      setValue('zip_code', pincode || '');
      setValue('country', country || 'India');
      setValue('state', state || '');
      setValue('city', city || '');
      if (Array.isArray(selectedServiceProvider.serviceProviderWorkTypeMapping)) {
        const matched = selectedServiceProvider?.serviceProviderWorkTypeMapping
          ?.map((item) => {
            // Find the corresponding workType in AllWorkTypes
            const workType = AllWorkTypes?.find((wt) => wt.id === item.workType_id);
            return workType ? { id: workType.id, label: workType.name } : null;
          })
          .filter(Boolean);

        setValue('subCategory', matched || []);
      } else {
        setValue('subCategory', []);
      }

      setValue('is_active', is_active || false);

      // Trigger pincode-based state/city population only if pincode exists and is 6 digits
      if (pincode && String(pincode).length === 6) {
        handlePincodeChange(String(pincode));
      }
    }
  }, [modalType, selectedServiceProvider, setValue, handlePincodeChange, AllWorkTypes]);

  // Form submission handler
  const onSubmitForm = (data) => {
    if (parseInt(data.category_id) === 1 && (!data.subCategory || data.subCategory.length < 1)) {
      toast.error('Please select subCategory');
      return;
    }

    const formattedData = {
      phone_number: data.phone_number,
      name: data.name,
      category_id: data.category_id,
      type: data.type_id,
      is_active: false,
      pincode: data.zip_code,
      country: data.country,
      state: data.state,
      city: data.city,
      workTypes:
        data.category_id && data.category_id === 1
          ? data.subCategory?.filter(Boolean).map((item) => item.id)
          : null
    };

    if (data.email) {
      formattedData.email = data.email;
    }

    console.log(formattedData, 'formattedData');
    onSubmit(formattedData);
  };

  // Handler to sanitize the phone number input
  const handlePhoneNumber = useCallback(
    (val) => {
      const newVal = val.replace(/[^0-9]/g, '');
      setValue('phone_number', newVal);

      // Check if service provider exists in marketplace when 10 digits are entered (only for adding, not editing)
      if (newVal.length === 10 && modalType === 'Add') {
        setIsCheckingSP(true);
        checkSPMutation(newVal);
      }
    },
    [setValue, checkSPMutation, modalType]
  );

  // Handle import service provider
  const handleImportSP = () => {
    setOpenAddUser(false);
    if (marketplaceSP) {
      importSPMutation({ serviceProvider_id: marketplaceSP?.data?.id });
    }
  };

  // Handle cancel import - prefill form with marketplace data
  const handleCancelImport = () => {
    setShowImportModal(false);
    setOpenAddUser(false);
    setMarketplaceSP(null);
  };

  // Handler to sanitize the name input (only alphabets and spaces allowed)
  const handleName = useCallback(
    (val) => {
      const newVal = val.replace(/[^a-zA-Z\s]/g, '');
      setValue('name', capitalizeWords(newVal));
    },
    [setValue]
  );

  // Handler for email validation; sets the value if valid
  const handleEmailValidation = useCallback(
    (val) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(val)) {
        setValue('email', val);
      } else {
        console.log('Invalid email format');
      }
    },
    [setValue]
  );

  return (
    <>
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
          Service Provider Details
        </span>
      </div>
      <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-4 pt-6">
        {/* Basic Details */}
        <div className="flex justify-between items-center w-full">
          {' '}
          <FormInput
            width={100}
            label={'Phone Number'}
            register={register}
            isRequired={true}
            register_key={'phone_number'}
            type={'text'}
            maxLength={10}
            errors={errors.phone_number}
            onType={handlePhoneNumber}
            readOnly={modalType && modalType === 'Edit'}
            disabled={isCheckingSP}
            placeholder={isCheckingSP ? 'Checking marketplace...' : 'Enter phone number'}
          />
        </div>
        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label={'Full Name'}
            isRequired={true}
            register_key={'name'}
            register={register}
            type={'text'}
            errors={errors.name}
            onType={handleName}
          />
          <FormInput
            width={48}
            label={'Email Address'}
            register={register}
            isRequired={false}
            register_key={'email'}
            type={'text'}
            errors={errors.email}
            onType={handleEmailValidation}
          />
        </div>
        <div className="flex justify-between items-center w-full flex-wrap gap-4">
          <FormInput
            width={48}
            label={'Service Provider Category'}
            register={register}
            isRequired={true}
            register_key={'category_id'}
            type={'dropdown'}
            options={[
              { value: '', label: 'Select Category', disabled: true, selected: true },
              ...categoriesForDropdown.map((item) => ({
                value: item.id,
                label: item.name.trim()
              }))
            ]}
            errors={errors.category_id}
          />

          {parseInt(watch('category_id')) === 1 && (
            <div className="w-[48%]">
              <div className="flex justify-between items-start space-x-2 w-fit mb-2">
                <span className="text-gray-600">SubCategory</span>{' '}
                <span className="text-red-500">*</span>
              </div>
              <Controller
                control={control}
                name="subCategory"
                render={({ field: { onChange, value } }) => (
                  <Multiselect
                    options={CategoryOptions}
                    selectedValues={value} // Pass the current value from form control
                    displayValue="label"
                    onSelect={(selectedList) => onChange(selectedList)}
                    onRemove={(selectedList) => onChange(selectedList)}
                  />
                )}
              />

              {errors.subCategory && (
                <p className="text-red-500 text-sm">{errors.subCategory.message}</p>
              )}
            </div>
          )}

          <FormInput
            width={48}
            label={'Service Provider Type'}
            register={register}
            isRequired={true}
            register_key={'type_id'}
            type={'dropdown'}
            options={[
              { value: '', label: 'Select Type' },
              { value: 'individual', label: 'Individual' },
              { value: 'company', label: 'Company' }
            ]}
            errors={errors.type_id}
          />
        </div>

        {/* Address Details */}
        <div className="flex flex-col justify-between items-start gap-4">
          <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
            Address Details
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between space-x-4">
            <FormInput
              width={48}
              register={register}
              isRequired
              register_key={'zip_code'}
              label={'Zip/Pin Code'}
              type={'text'}
              maxLength={6}
              errors={errors.zip_code}
              onType={handlePincodeChange}
            />
            <FormInput
              width={48}
              register={register}
              isRequired
              register_key={'country'}
              label={'Select Country'}
              valuePre={'India'}
              errors={errors.country}
              CustomStyle={{ pointerEvents: 'none' }}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between space-x-4">
            <FormInput
              width={48}
              register={register}
              isRequired
              register_key={'state'}
              label={'State'}
              errors={errors.state}
              valuePre={state}
              CustomStyle={{ pointerEvents: 'none' }}
            />
            <FormInput
              width={48}
              register={register}
              isRequired
              register_key={'city'}
              label={'City'}
              defaultValuePre={city}
              type={'dropdown'}
              options={cityDropdownOptions}
              errors={errors.city}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="w-full flex justify-end items-center space-x-4 mt-8">
          <Button type="button" onClick={onClose} displayType="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {modalType === 'Add' ? 'Add' : 'Update'}
          </Button>
        </div>
      </form>

      {/* Import Confirmation Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {spMessage || 'Service Provider Found'}
              </h3>
            </div>

            <div className="mb-4">
              <p className="text-gray-600 mb-2">
                {spMessage?.includes('your list')
                  ? 'A service provider with this phone number already exists in your list:'
                  : 'A service provider with this phone number already exists in the marketplace:'}
              </p>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="font-medium text-gray-900">
                  {marketplaceSP?.data?.user?.name || 'N/A'}
                </p>
                <p className="text-sm text-gray-600">
                  {marketplaceSP?.data?.user?.phone_number || 'N/A'}
                </p>
                <p className="text-sm text-gray-600">{marketplaceSP?.data?.user?.email || 'N/A'}</p>
              </div>
              <p className="text-gray-600 mt-2">
                {spMessage?.includes('your list')
                  ? 'This service provider is already in your list.'
                  : 'Would you like to import this service provider instead of creating a new one?'}
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <Button displayType="secondary" onClick={handleCancelImport} disabled={isImporting}>
                Cancel
              </Button>
              {spMessage?.includes('marketplace') && (
                <Button onClick={handleImportSP} disabled={isImporting}>
                  {isImporting ? 'Importing...' : 'Import Service Provider'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddServiceProvider;
